import { useContext, useEffect } from "react";

import { useMapEvent, useMapEvents } from "react-leaflet";

import { isEmpty, debounce } from "lodash";

import { useRouter } from "next/router";

import { useMediaQuery } from "@/storybook-components";

import { EsriContext } from "../context/esri-context";
import { EsriContextType } from "../context/esri-context-type";

import { useAppSelector, useAppDispatch } from "@/redux/store/hooks";
import {
  setMapCenterCoordinates,
  setPinDetails,
  setScrollPosition
} from "@/redux/features/place/slice";
import {
  setPinSearchFromUrl,
  setView
} from "@/redux/features/place-right-panel/slice";
import {
  resetIsMarkerInsidePolygon,
  resetMarker
} from "@/redux/features/place-marker/slice";

import { fetchPinDetails } from "@/redux/features/place-markers/thunk";
import { updateMapBounds } from "@/redux/features/place/slice";
import {
  setCurrentPageNumber,
  setPreviousMarker
} from "@/redux/features/place-marker/slice";
import { setMarkers } from "@/redux/features/place-markers/slice";

const SORT_ORDER = {
  newest: "created_at",
  topPins: "votes_count"
};

function SetViewOnClick() {
  const dispatch = useAppDispatch();
  const rightPanelView = useAppSelector(
    (state) => state.placesRightPanelView.view
  );
  // eslint-disable-next-line no-unused-vars
  const { setActionsVisibility, setInteractionsVisibility, mapRef } =
    useContext(EsriContext) as EsriContextType;
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const view = useAppSelector((state) => state.placesRightPanelView.view);
  const pinDetail = useAppSelector((state) => state.place.pinDetails);
  const centerCoordinates = useAppSelector(
    (state) => state.place.centerCoordinates
  );
  const pinSearchFromUrl = useAppSelector(
    (state) => state.placesRightPanelView.pinSearchFromUrl
  );
  const scrollPosition = useAppSelector((state) => state.place.scrollPostion);
  const projectId = useAppSelector((state) => state.project.project.id);
  const placesId = useAppSelector((state) => state.place.place.id);
  const filteredCategoriesData = useAppSelector(
    (state) => state.placeMarkersSlice.filteredCategoriesData
  );
  const { categories, isSelfPin, sortOrder } = filteredCategoriesData;
  const { id: userId } = useAppSelector(
    (state) => state.user.registeredUser.profile
  );
  const { prevMarkers } = useAppSelector((state) => state.placesMarkerSlice);
  const router = useRouter();
  const { asPath } = router;
  useMapEvent("click", () => {
    if (rightPanelView !== "create-pin") {
      if (!isEmpty(pinDetail)) dispatch(setPinDetails({}));

      if (!isEmpty(centerCoordinates)) dispatch(setMapCenterCoordinates({}));
      dispatch(resetMarker());

      if (view !== "activity-feed") dispatch(setView("activity-feed"));
      if (isMobile) {
        setActionsVisibility(false);
        setInteractionsVisibility(true);
      }
      dispatch(setScrollPosition(0));
      dispatch(resetIsMarkerInsidePolygon());
      const updatedPath = asPath?.split("?")?.[0];
      dispatch(setPinSearchFromUrl(false));
      router.replace(updatedPath, "", { shallow: true });
      if (prevMarkers?.length || pinSearchFromUrl) {
        dispatch(setPreviousMarker([]));
        dispatch(
          fetchPinDetails({
            projectId,
            placesId,
            filters: {
              ...(isSelfPin && userId && { user_id: userId }),
              sort: { created_at: "desc" }
            }
          })
        );
      }
    }
  });

  const filterPins = async (map) => {
    const { _southWest, _northEast } = map.getBounds();

    const bounds = {
      lat_min: _southWest.lat,
      lat_max: _northEast.lat,
      lng_min: _southWest.lng,
      lng_max: _northEast.lng
    };

    dispatch(updateMapBounds(bounds));

    if (!isMobile && prevMarkers.length) {
      await dispatch(setMarkers(prevMarkers));

      await document
        .getElementsByClassName("view-pin-details-scroller")?.[0]
        ?.scroll({ top: scrollPosition, behavior: "smooth" });

      if (
        document.getElementsByClassName("view-pin-details-scroller")?.[0]
          ?.scrollTop === scrollPosition &&
        rightPanelView === "activity-feed"
      ) {
        dispatch(setPreviousMarker([]));
      }
    } else {
      try {
        dispatch(setCurrentPageNumber(1));
        if (rightPanelView !== "view-pin-details") {
          dispatch(setPreviousMarker([]));
          dispatch(
            fetchPinDetails({
              projectId,
              placesId,
              filters: {
                ...(isSelfPin && userId && { user_id: userId }),
                sort: { [SORT_ORDER[sortOrder]]: "desc" },
                category_id: categories
              }
            })
          );
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  const debouncedFilterPins = debounce(filterPins, 500);

  const map = useMapEvents({
    dragend: (e) => debouncedFilterPins(e.target),
    zoomend: (e) => debouncedFilterPins(e.target),
    moveend: (e) => debouncedFilterPins(e.target)
  });

  useEffect(() => {
    debouncedFilterPins(map);
  }, []);

  return null;
}

export default SetViewOnClick;
